import React, { createContext, ReactElement, useContext, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { getAuthStorage, removeAuthStorage, setAuthStorage } from '../services/storage';
import { STORAGE_KEYS } from '../constants/Config';

// Interface que corresponde ao IUsuario do backend
interface GrupoType {
  id: number;
  nome: string;
}

// Permissões de tela do usuário
export interface ITelaPermissaoUsuario {
  codigo: string;
  path: string;
  acoes: string[];
}

interface DepartamentoType {
  id: number;
  nome: string;
}

interface UserType {
  id: number;
  nome: string;
  nome_social?: string;
  email: string;
  cpf?: string;
  data_nascimento?: string;
  ativo: boolean;
  is_sympla: boolean;
  imagem_url?: string;
  grupo_id: number;
  grupo?: GrupoType;
  departamento_id?: number | null;
  departamento?: DepartamentoType | null;
  telas_permissoes?: ITelaPermissaoUsuario[];
  grupos_empresas_permitidos?: string[];
}

interface msgType {
  Error: string;
}

interface ErrorType {
  status: string;
  msg: msgType;
}

interface AuthContextData {
  signed: boolean;
  user: UserType | object | any;
  loading: boolean;
  error: ErrorType | null;
  acessoDelegado: boolean;
  adminSolicitanteNome: string | null;
  // eslint-disable-next-line no-unused-vars
  Login(user: object): Promise<void> | any;
  // eslint-disable-next-line no-unused-vars
  LoginDelegado(email: string, token: string): Promise<void> | any;
  Logout(): void;
  getUser(): any;
  // Funções de permissão
  // eslint-disable-next-line no-unused-vars
  hasPermission(path: string, acao?: string): boolean;
  // eslint-disable-next-line no-unused-vars
  canAccessGrupoEmpresa(nomeGrupoEmpresa: string): boolean;
  isAdmin(): boolean;
  isTechnical(): boolean;
  canViewAuditoria(): boolean;
  temVisaoTodosGrupos(): boolean;
}

interface AuxProps {
  children: ReactElement | ReactElement[];
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC<AuxProps> = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserType | object | null>(null);
  const [error, setError] = useState<ErrorType | null | any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [signed, setSigned] = useState<boolean>(false);
  const [acessoDelegado, setAcessoDelegado] = useState<boolean>(false);
  const [adminSolicitanteNome, setAdminSolicitanteNome] = useState<string | null>(null);

  useEffect(() => {
    const verifyLogged = async () => {
      if (user == null) {
        const storageAuth = await getAuthStorage();
        if (storageAuth) {
          api.defaults.headers.common.Authorization = `Bearer ${storageAuth.access_token}`;
          setSigned(true);
          // Verifica se é acesso delegado
          if (storageAuth.acesso_delegado) {
            setAcessoDelegado(true);
            setAdminSolicitanteNome(storageAuth.admin_solicitante_nome || null);
          }
          try {
            await getUser();
          } catch (networkError: any) {
            // Se for erro de rede, não faz logout automático
            // apenas desativa o loading e mantém o usuário logado
            if (networkError.message === 'Network Error' || !networkError.response) {
              // Mantém o usuário logado mesmo com erro de rede
              // Permite que a aplicação continue funcionando
              // Não define um usuário inválido, deixa null para evitar problemas
              // A aplicação deve lidar com user null quando necessário
            } else {
              // Para outros erros (401, etc), remove o token
              await removeAuthStorage();
              setSigned(false);
            }
          }
        }
      }
      setLoading(false);
    };
    verifyLogged();
  }, [user]);

  const getUser: AuthContextData['getUser'] = async () => {
    const res = await api.get('/api/v1/usuarios/perfil/');

    if (res.status !== 200) {
      return false;
    }
    const userData = res.data.data;
    setUser(userData);
    return userData;
  };

  const Login: AuthContextData['Login'] = async data => {
    try {
      setLoading(true);
      setError(null);

      // Definir o header de autorização
      api.defaults.headers.common.Authorization = process.env.REACT_APP_BASIC_ADMIN as string;

      // Requisição POST — IP é extraído automaticamente pelo backend via X-Forwarded-For
      // Em dev local, manda client_ip pra contornar captcha via whitelist
      const loginUrl =
        process.env.NODE_ENV === 'development'
          ? '/api/v1/login/?client_ip=177.136.201.93'
          : '/api/v1/login/';
      const res = await api.post(loginUrl, {
        grant_type: 'password',
        ...data,
      });

      setSigned(true);

      // Salvar o token no storage
      await setAuthStorage(res.data.data);

      // Configurar o token no header
      api.defaults.headers.common.Authorization = `Bearer ${res.data.data.access_token}`;

      // Obter os dados do usuário
      await getUser();

      setLoading(false);
      navigate('/', { replace: true });
    } catch (e) {
      const err = e as AxiosError;

      setError(err.response?.data);
      setLoading(false);
      throw err;
    }
  };

  const LoginDelegado: AuthContextData['LoginDelegado'] = async (email, tokenAcesso) => {
    try {
      setLoading(true);
      setError(null);

      const res = await api.post('/api/v1/login/acesso-delegado/', {
        email,
        token_acesso: tokenAcesso,
      });

      setSigned(true);

      // Salvar o token no storage
      await setAuthStorage(res.data.data);

      // Configurar o token no header
      api.defaults.headers.common.Authorization = `Bearer ${res.data.data.access_token}`;

      // Marcar como acesso delegado
      if (res.data.data.acesso_delegado) {
        setAcessoDelegado(true);
        setAdminSolicitanteNome(res.data.data.admin_solicitante_nome || null);
      }

      // Obter os dados do usuário
      await getUser();

      setLoading(false);
      navigate('/', { replace: true });
    } catch (e) {
      const err = e as AxiosError;
      setError(err.response?.data);
      setLoading(false);
      throw err;
    }
  };

  function Logout() {
    removeAuthStorage();
    // Limpar chat do storage
    localStorage.removeItem(STORAGE_KEYS.CHAT_MESSAGES);
    localStorage.removeItem(STORAGE_KEYS.CHAT_SETTINGS);
    // Limpar estado de acesso delegado
    setAcessoDelegado(false);
    setAdminSolicitanteNome(null);
    // Redirecionar antes de limpar o state para evitar flash do 404
    window.location.replace('/login');
  }

  // Verifica se o usuário é admin (grupo_id == 1)
  function isAdmin(): boolean {
    const currentUser = user as UserType;
    return currentUser?.grupo_id === 1;
  }

  // Acesso técnico: Admin (1) ou Desenvolvimento (5).
  // Alinhado com `_ADMIN_GRUPO_IDS` do especialistas-backend.
  function isTechnical(): boolean {
    const currentUser = user as UserType;
    return currentUser?.grupo_id === 1 || currentUser?.grupo_id === 5;
  }

  // Acesso à auditoria de fidelidade (lupa da IA): Admin (1), Desenvolvimento (5),
  // Gestão (4) e Societário (10). Espelha `_AUDITORIA_GRUPO_IDS` do backend — gate
  // PRÓPRIO, não alarga `isTechnical()` (protege outras telas técnicas). 2026-07-02.
  function canViewAuditoria(): boolean {
    const currentUser = user as UserType;
    const g = currentUser?.grupo_id;
    return g === 1 || g === 5 || g === 4 || g === 10;
  }

  // Visão de todos os grupos de empresas (admin ou sem restrição de grupos).
  // Espelha a semântica de `visualizar_todos_grupos_empresas` do backend:
  // libera as telas multi-empresa (carteira/escritório) da Auditoria Inteligente.
  function temVisaoTodosGrupos(): boolean {
    const currentUser = user as UserType;
    if (currentUser?.grupo_id === 1) return true;
    const gruposPermitidos = currentUser?.grupos_empresas_permitidos ?? [];
    // Lista vazia = herança total (mesma regra de `canAccessGrupoEmpresa`).
    return gruposPermitidos.length === 0;
  }

  // Verifica se o usuário tem permissão para uma tela/ação
  function hasPermission(path: string, acao?: string): boolean {
    const currentUser = user as UserType;

    // Admin tem todas as permissões
    if (currentUser?.grupo_id === 1) return true;

    // Verifica nas permissões de tela
    const telasPermissoes = currentUser?.telas_permissoes ?? [];
    const permissaoAtendeAcao = (tela: ITelaPermissaoUsuario): boolean =>
      !acao ? tela.acoes.length > 0 : tela.acoes.includes(acao);

    // Tenta encontrar correspondência exata primeiro
    let telaPermissao = telasPermissoes.find(t => t.path === path);

    // Se não encontrou, tenta correspondência com path base (sem parâmetros)
    if (!telaPermissao) {
      // Remove parâmetros dinâmicos do path (ex: /grupos/permissoes/:itemId -> /grupos/permissoes)
      const pathBase = path.replace(/\/:[^/]+/g, '');
      telaPermissao = telasPermissoes.find(t => {
        const telaPathBase = t.path.replace(/\/:[^/]+/g, '');
        return telaPathBase === pathBase || t.path === pathBase;
      });
      // Rota com parametro sem tela propria vale como o path base, inclusive para quem so' tem sub-telas dele
      // (a pagina do evento, `/eventos/:eventoId`, para quem so' tem o `/eventos/kanban`).
      if (!telaPermissao && pathBase !== path) return hasPermission(pathBase, acao);
    }

    if (telaPermissao) {
      return permissaoAtendeAcao(telaPermissao);
    }

    // Se não encontrou permissão direta, verifica se tem permissão em alguma sub-tela
    // (para mostrar módulos pai quando tem permissão em sub-módulos)
    const hasSubPermission = telasPermissoes.some(
      t => t.path.startsWith(`${path}/`) && permissaoAtendeAcao(t),
    );
    if (hasSubPermission) return true;

    // Se não encontrou, verifica se tem permissão em um path pai
    // (para herdar permissão do pai para os filhos - ex: /empresas dá acesso a /empresas/listagem)
    const hasParentPermission = telasPermissoes.some(
      t => path.startsWith(`${t.path}/`) && permissaoAtendeAcao(t),
    );
    if (hasParentPermission) return true;

    return false;
  }

  // Verifica se o usuário pode acessar um grupo de empresa
  function canAccessGrupoEmpresa(nomeGrupoEmpresa: string): boolean {
    const currentUser = user as UserType;

    // Admin pode acessar todos
    if (currentUser?.grupo_id === 1) return true;

    // Verifica nos grupos permitidos (agora é uma lista de strings)
    const gruposPermitidos = currentUser?.grupos_empresas_permitidos ?? [];

    // Se não tem grupos definidos, permite acesso a todos (herança total)
    if (gruposPermitidos.length === 0) return true;

    return gruposPermitidos.includes(nomeGrupoEmpresa);
  }

  return (
    <AuthContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        signed,
        user,
        loading,
        error,
        acessoDelegado,
        adminSolicitanteNome,
        Login,
        LoginDelegado,
        Logout,
        getUser,
        hasPermission,
        isTechnical,
        canViewAuditoria,
        temVisaoTodosGrupos,
        canAccessGrupoEmpresa,
        isAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider.');
  }

  return context;
}
